/**
 * Linkify bare http(s) URLs in Reviews.io Polaris *question answers* only.
 * Re-runs on widget DOM updates (tab switch, pagination, Vue re-renders).
 */
(function reviewsQuestionsLinkify() {
  'use strict';

  const DEBOUNCE_MS = 150;
  /** Broad match; `safeHttpUrl` trims trailing punctuation / invalid tails. */
  const URL_LIKE = /\bhttps?:\/\/[^\s<>"'`\]]+/gi;

  function isTileClubHost(hostname) {
    const h = String(hostname || '').toLowerCase();
    return h === 'tileclub.com' || h.endsWith('.tileclub.com');
  }

  function safeHttpUrl(candidate) {
    let s = String(candidate || '').trim();
    if (!s) return null;
    for (let len = s.length; len >= 8; len -= 1) {
      try {
        const u = new URL(s.slice(0, len));
        if (u.protocol === 'http:' || u.protocol === 'https:') {
          return { url: u, consumed: len };
        }
      } catch {
        // continue trimming
      }
    }
    return null;
  }

  /** Answer body nodes only: text block after question heading inside each Q&A row. */
  function findQuestionsPanel(scope) {
    return (
      scope.querySelector('[id="questions"][role="tabpanel"]') ||
      scope.querySelector('[aria-labelledby="questions-tab"][role="tabpanel"]') ||
      scope.querySelector('.ElementsWidget__list#questions')
    );
  }

  function collectAnswerBodies(panel) {
    const out = [];
    const seen = new Set();
    panel.querySelectorAll('.item__review .u-marginBottom--md').forEach((md) => {
      const heading = md.querySelector(':scope > .R-TextHeading');
      if (!heading) return;

      let sib = heading.nextElementSibling;
      while (sib && !sib.matches('.R-TextBody')) {
        sib = sib.nextElementSibling;
      }
      const body = sib;
      if (body && !seen.has(body)) {
        seen.add(body);
        out.push(body);
      }
    });
    return out;
  }

  function configureAnchor(a, urlObj) {
    a.href = urlObj.href;
    const host = urlObj.hostname || '';
    if (isTileClubHost(host)) {
      a.removeAttribute('target');
      a.removeAttribute('rel');
    } else {
      a.target = '_blank';
      a.rel = 'nofollow noopener noreferrer ugc external';
    }
    a.classList.add('u-textDecoration--underline');
  }

  function linkifyTextNode(textNode) {
    const parentEl = textNode.parentElement;
    if (!parentEl || parentEl.closest('a')) return;

    const text = textNode.nodeValue;
    if (!text || !/https?:\/\//i.test(text)) return;

    URL_LIKE.lastIndex = 0;
    let m;
    let last = 0;
    const frag = document.createDocumentFragment();
    let changed = false;

    while ((m = URL_LIKE.exec(text)) !== null) {
      const raw = m[0];
      const parsed = safeHttpUrl(raw);

      if (!parsed) {
        const plain = text.slice(last, m.index + raw.length);
        if (plain) frag.appendChild(document.createTextNode(plain));
        last = m.index + raw.length;
        URL_LIKE.lastIndex = last;
        changed = true;
        continue;
      }

      const before = text.slice(last, m.index);
      if (before) frag.appendChild(document.createTextNode(before));

      const display = raw.slice(0, parsed.consumed);
      const a = document.createElement('a');
      a.textContent = display;
      configureAnchor(a, parsed.url);
      frag.appendChild(a);

      last = m.index + display.length;
      changed = true;
      URL_LIKE.lastIndex = last;
    }

    if (!changed) return;

    const tail = text.slice(last);
    if (tail) frag.appendChild(document.createTextNode(tail));

    parentEl.replaceChild(frag, textNode);
  }

  function linkifyArticleRoot(root) {
    if (!root) return;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        if (!node.nodeValue || !/https?:\/\//i.test(node.nodeValue)) {
          return NodeFilter.FILTER_REJECT;
        }
        const p = node.parentElement;
        if (!p || p.closest('a')) return NodeFilter.FILTER_REJECT;
        const tag = p.tagName;
        if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'NOSCRIPT' || tag === 'CODE' || tag === 'PRE') {
          return NodeFilter.FILTER_REJECT;
        }
        return NodeFilter.FILTER_ACCEPT;
      },
    });
    const batch = [];
    let n = walker.nextNode();
    while (n) {
      batch.push(n);
      n = walker.nextNode();
    }
    batch.forEach(linkifyTextNode);
  }

  function findReviewsWidgetRoot() {
    return (
      document.querySelector('reviewsio-product-reviews-widget') ||
      document.querySelector('[id*="reviews_io_reviewsio_polaris"]')
    );
  }

  function scan() {
    const widget = findReviewsWidgetRoot();
    if (!widget) return;
    const panel = findQuestionsPanel(widget);
    if (!panel) return;
    const bodies = collectAnswerBodies(panel);
    bodies.forEach(linkifyArticleRoot);
  }

  let t = null;
  let mo = null;

  function schedule() {
    if (t) clearTimeout(t);
    t = setTimeout(() => {
      t = null;
      scan();
    }, DEBOUNCE_MS);
  }

  function attachObserver(root) {
    if (mo) mo.disconnect();
    mo = new MutationObserver(schedule);
    mo.observe(root, { childList: true, subtree: true, characterData: true });
    schedule();
  }

  function boot() {
    const tryWidget = () => findReviewsWidgetRoot();
    const w = tryWidget();
    if (w) {
      attachObserver(w);
      return;
    }
    const wait = new MutationObserver(() => {
      const found = tryWidget();
      if (found) {
        wait.disconnect();
        attachObserver(found);
      }
    });
    wait.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
